import { DateTime } from "luxon";
import type { Department } from "../contexts/AuthProvider";
import type { HospitalDataContextType } from "../contexts/HospitalDataProvider";
import { getAutoSchedulerShiftStaffingIssues, getNurseAutoSchedulerStaffingIssues } from "./autoScheduler";
import { formatNurseName, formatPersonFullName, isNurseActiveAnywhere } from "./formatters";
import {
	type Day,
	type DepartmentLocation,
	DepartmentNames,
	EmploymentType,
	type HospitalData,
	hppdTargetForDate,
	IssuePriority,
	IssueType,
	NotificationType,
	type Nurse,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseInShift,
	NurseRole,
	NurseTrainingStatus,
	type OpenShiftRequest,
	Preferences,
	type ReplaceShiftRequest,
	RequestResponsesStatusOptions,
	RequestStatus,
	type ScheduleData,
	type Shift,
	type ShiftTimes,
	ShiftType,
	type SpecificPreference,
	SuggestionAction,
	type WeekData,
} from "./models";
import { COUNT_ALL_TRAINING, trainingPpdCountingOf } from "./trainingPpdCounting";
import {
	getCensus,
	getNurseChanges,
	getNursesHoursData,
	getSnfWantedNursesHours,
	getTotalHours,
	getWage,
	inverseRolesMap,
	isCNARegulationCompliant,
	isNursesRegulationCompliant,
	isOverstaffed,
	isOverstaffedSnf,
	isUnderstaffed,
	isUnderstaffedSnf,
	type RoleKey,
	rolesMap,
} from "./utilFunctions";
export const MAX_CONSECUTIVE_ALLOWED_SHIFTS = 4;

enum NurseGroup {
	FullTimeOrPartTimeAvailable = 1,
	FloatPoolAvailable = 2,
	FullTimeOrPartTimeFullyBooked = 3,
	FloatPoolFullyBooked = 4,

	PerDiemOrTravelAvailable = 5,
	PerDiemOrTravelFullyBooked = 6,

	FullTimeOrPartTimeOverBooked = 8,
	FloatPoolOverBooked = 9,
	PerDiemOrTravelOverBooked = 10,

	AdjacentShift = 11,

	ShiftUnavailable = 12,
}

export interface ShiftDetails {
	weekIndex: number;
	shiftNo: number;
	dayNo: number;
	date: DateTime;
	startTime: DateTime;
	endTime: DateTime;
}
export interface ShiftKey {
	role: string;
	date: DateTime;
	shiftType: ShiftType;
	dayNo: number;
	shiftNo: number;
	startTime: DateTime;
	endTime: DateTime;
}

export interface Suggestion {
	action: SuggestionAction;
	nurseId: number;
	dayNo: number;
	shiftNo: number;
	weekIndex: number;
	otherNurseId?: number;
	otherDayNo?: number;
	otherShiftNo?: number;
	isAccepted?: boolean;
	issueType?: IssueType;
	displayReject?: boolean;
	displayNurse?: boolean;
	customDisplayText?: string;
	requestId?: string;
	shiftId?: string;
}

export interface StaffingIssue {
	issueType: IssueType;
	priority: IssuePriority;
	title: any[];
	suggestions: Suggestion[];
	implemented?: boolean;
	notificationId?: string;
	shiftTimes?: ShiftTimes;
	notificationType?: NotificationType;
}

/**
 * The issues without their swap suggestions. Applying a swap only marks it in the old list, and a grid reading the
 * schedule cache never shows it, so with the cache on a swap isn't offered.
 */
export const withoutSwapSuggestions = (issues: StaffingIssue[]): StaffingIssue[] =>
	issues.map((issue) => ({ ...issue, suggestions: issue.suggestions.filter((suggestion) => suggestion.action !== SuggestionAction.Swap) }));

export interface StaffingParams {
	hospitalData: ScheduleData;
	weekIndex: number;
	isSnf: boolean;
	isAutoScheduler: boolean;
	chosenDepartment: Department;
	timezone: string;
	departmentNurseTypes: NurseFullRole[];
	requests: (OpenShiftRequest | ReplaceShiftRequest)[];
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"];
	jobManagementEnabled?: boolean;
	/** The department's name-order flag (`useLastNameFirst()`), for the staff names in issue titles. */
	lastNameFirst: boolean;
}

export interface StaffingContext {
	hospitalData: ScheduleData;
	currWeek: WeekData;
	weekIndex: number;
	chosenDepartment: Department;
	nurseMap: Map<number, Nurse>;
	nurseOcuurences: Record<number, number>;
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"];
	lastNameFirst: boolean;
}

export const getStaffingIssues = ({
	hospitalData,
	weekIndex,
	isSnf,
	isAutoScheduler,
	chosenDepartment,
	timezone,
	departmentNurseTypes,
	requests,
	calculateShiftTimes,
	jobManagementEnabled = false,
	lastNameFirst,
}: StaffingParams): StaffingIssue[] => {
	// will present general issues about the staffs
	// 1. Overstaffed/understaffed per shift per day
	// 2 in case there's a nurse that picked shift for specific date, and there's a nurse in this shift that is OT/agency with higher predicated price
	if (weekIndex >= hospitalData.weeks.length) {
		return [];
	}
	const currWeek = hospitalData.weeks[weekIndex];
	const nurseMap: Map<number, Nurse> = new Map(hospitalData.nurses.map((nurse) => [nurse.id, nurse]));
	const nurseOcuurences = getNurseShiftOccurrencesByWeek(hospitalData, weekIndex);
	const isLowry = chosenDepartment.department_id === DepartmentNames.CenterAtLowryMainDept;
	const context: StaffingContext = {
		hospitalData,
		weekIndex,
		chosenDepartment,
		nurseMap,
		nurseOcuurences,
		currWeek,
		calculateShiftTimes,
		lastNameFirst,
	};
	const today = DateTime.now();

	let shiftIssues: StaffingIssue[];
	if (isSnf) {
		shiftIssues = getSnfShiftStaffingIssues(context, departmentNurseTypes, departmentNurseTypes, timezone, isLowry, jobManagementEnabled);
	} else if (isAutoScheduler) {
		shiftIssues = getAutoSchedulerShiftStaffingIssues(context);
	} else {
		shiftIssues = getShiftStaffingIssues(context, {
			isSnf,
			isAutoScheduler,
			timezone,
			isLowry,
		});
	}

	const role = isSnf ? NurseRole.CNA : NurseRole.RN;
	let nurseIssues: StaffingIssue[];

	if (isAutoScheduler) {
		nurseIssues = getNurseAutoSchedulerStaffingIssues(context, role, departmentNurseTypes, timezone, isLowry);
	} else {
		nurseIssues = getNurseStaffingIssues(context, role, departmentNurseTypes, requests, timezone, isLowry, calculateShiftTimes, jobManagementEnabled);
	}

	// Combine shift and nurse issues
	const allIssues: StaffingIssue[] = [...shiftIssues, ...nurseIssues];

	// Filter out past issues - only keep issues without shift times or with dates today or in the future
	const currentAndFutureIssues = allIssues.filter((issue: StaffingIssue) => !issue.shiftTimes || issue.shiftTimes.start >= today);

	// Return sorted issues by date
	return sortIssues(currentAndFutureIssues);
};

function sortIssues(issues: StaffingIssue[]): StaffingIssue[] {
	// Sort issues by date and shift type
	return issues.sort((a: StaffingIssue, b: StaffingIssue) => {
		const startA = a.shiftTimes?.start;
		const startB = b.shiftTimes?.start;

		if (!startA && !startB) return 0; // Both are missing, consider equal
		if (!startA) return 1; // 'a' is missing, sort it after 'b'
		if (!startB) return -1; // 'b' is missing, sort it after 'a'

		return startA.diff(startB).toMillis();
	});
}

export const sortNurses = (
	hospitalData: ScheduleData,
	shiftNo: number,
	weekIndex: number,
	dayNo: number,
	nurseOccurrences: Record<number, number>,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
	isSortForRemoval?: boolean,
	nurseRole: NurseRole = NurseRole.RN,
	isAutoScheduler = false,
	nursesPool: Nurse[] = [],
): Nurse[] => {
	// WARNING OUTATED
	// Sorting logic
	//
	// Terminology:
	// "Regular" nurses - (full time / part time)
	// "Other" nurses (pool_nurses / travel / per_diem)
	//
	// Filter out:
	//       Already in the shift
	//
	// Sort
	// 0. RN are before CNA
	// 1. Full / part time nurses that have fewer shifts then their wanted amount. Within this group, subsort:
	//  1.1. Shifts_they_have / wanted_shift_amount (lower is better, so nurse with 1/2 shifts will be below one with 1/3 shifts and above one with 2/3 shifts)
	//  1.2 wanted_shift_amount (Full_time is higher then part_time)
	//  1.3. reliability_score / hourly_wage
	// 2."Other" nurses (pool_nurses / travel / per_diem) with shifts up to their limit, subsort:
	//  2.1. reliability_score / hourly_wage
	// 3. Full / part time nurses with the wanted shifts amount, subsort:
	//  3.1. reliability_score / hourly_wage
	// 4. "Other" nurses with the wanted shifts amount, subsort:
	//  4.1. reliability_score / hourly_wage
	// 5. Full / part time nurses with more than wanted shifts amount, subsort:
	//  5.1. Optional: Their order should be by the "overtime amount"
	//  5.2. reliability_score / hourly_wage
	// 6. "Other" nurses with more than wanted shifts amount, subsort:
	//  6.1. Optional: Their order should be by the "overtime amount"
	//  6.2. reliability_score / hourly_wage
	// 7. Same as above for nurses that this shift is unavailable, subsort:
	//  7.1. the sort of 1-6
	// 8. Nurses who are already assigned to be following / previous shift, subsort:
	//  8.1. reliability_score / hourly_wage

	let availableNurses: Nurse[] = [];
	if (nursesPool.length === 0) {
		if (!hospitalData || shiftNo < 0) return [];
		const currentShiftNurseIds = getNurseIdsForShift(hospitalData, dayNo, shiftNo, weekIndex);
		availableNurses = hospitalData.nurses.filter(
			(nurse: Nurse) =>
				nurse.role === rolesMap[nurseRole] &&
				(isSortForRemoval ? currentShiftNurseIds.includes(nurse.id) : !currentShiftNurseIds.includes(nurse.id)) &&
				isNurseActiveAnywhere(nurse),
		);
	} else {
		availableNurses = nursesPool.filter((nurse: Nurse) => isNurseActiveAnywhere(nurse));
	}

	let returnedNurses = groupAndSortNurses(availableNurses, hospitalData, shiftNo, weekIndex, dayNo, nurseOccurrences, isAutoScheduler, calculateShiftTimes);
	if (isSortForRemoval) {
		returnedNurses = returnedNurses.reverse();
	}
	return returnedNurses;
};

const groupAndSortNurses = (
	nurses: Nurse[],
	hospitalData: any,
	shiftNo: number,
	weekIndex: number,
	dayNo: number,
	nurseOccurrences: Record<number, number>,
	isAutoScheduler = false,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
): Nurse[] => {
	const nurseOccurrencesWeekend: Record<number, number> | undefined = isAutoScheduler ? getNurseShiftOccurrencesByWeekend(hospitalData) : undefined;
	const groups: Nurse[][] = Array.from({ length: 13 }, () => []);

	nurses.forEach((nurse: Nurse) => {
		const groupIndex = determineGroupIndex(nurse, hospitalData, shiftNo, weekIndex, dayNo, nurseOccurrences, calculateShiftTimes);
		groups[groupIndex].push(nurse);
	});

	if (hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayNo]?.date) {
		return (
			// Remove duplicates
			groups
				.flatMap((group, index) => {
					return sortInnGroup(group, index + 1, nurseOccurrences, nurseOccurrencesWeekend, hospitalData, shiftNo, weekIndex, dayNo, calculateShiftTimes);
				})
				// Remove duplicates to make sure each nurse is only included once even though they should apear only once anyway
				.filter((nurse, index, self) => index === self.findIndex((t) => t.id === nurse.id))
		);
	}

	return groups.flat();
};

const determineGroupIndex = (
	nurse: Nurse,
	hospitalData: ScheduleData,
	shiftNo: number,
	weekIndex: number,
	dayNo: number,
	nurse_occurences: Record<number, number>,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
	addOccurrences = 0,
): NurseGroup => {
	// addOccurrences in case of checking what group the nurse will be if x shifts added
	if (isCannotWork(nurse, hospitalData, weekIndex, shiftNo, dayNo, calculateShiftTimes)) {
		return NurseGroup.ShiftUnavailable;
	}
	const occurrences = nurse_occurences[nurse.id] + addOccurrences;
	const wantedShifts: number = nurse?.allowedShifts ?? 0;
	const isHasAdjacentShifts = isNurseScheduledAdjacent(hospitalData, nurse.id, dayNo, shiftNo, weekIndex);
	const isFullTimeOrPartTime = isFullTimeOrPartTimeNurse(nurse);
	const isFloatPool = nurse.employmentType === EmploymentType.FloatPool;
	const isPerDiemOrTravel = isPerDiemOrTravelNurse(nurse);

	const isNightShift = shiftNo === 1;
	const prefersDayShift = nurse.shiftType === ShiftType.Day;
	const prefersNightShift = nurse.shiftType === ShiftType.Night;

	if ((prefersDayShift && isNightShift) || (prefersNightShift && !isNightShift)) {
		return isHasAdjacentShifts ? NurseGroup.AdjacentShift : NurseGroup.ShiftUnavailable;
	}

	// We need to rank the nurses based on their cost-effectiveness to the system, from the least expensive to the most expensive.
	// 1. full time & part time - fullTime and partTime
	// 2. float pool - floatPool
	// 3. per diem and agency contract - perDiem and travel

	// Available
	if (!isHasAdjacentShifts && isFullTimeOrPartTime && occurrences < wantedShifts) {
		return NurseGroup.FullTimeOrPartTimeAvailable;
	}

	if (!isHasAdjacentShifts && isFloatPool && occurrences < wantedShifts) {
		return NurseGroup.FloatPoolAvailable;
	}

	if (!isHasAdjacentShifts && isPerDiemOrTravel && occurrences < wantedShifts) {
		return NurseGroup.PerDiemOrTravelAvailable;
	}

	// Fully Booked
	if (!isHasAdjacentShifts && isFullTimeOrPartTime && occurrences === wantedShifts) {
		return NurseGroup.FullTimeOrPartTimeFullyBooked;
	}

	if (!isHasAdjacentShifts && isFloatPool && occurrences === wantedShifts) {
		return NurseGroup.FloatPoolFullyBooked;
	}

	if (!isHasAdjacentShifts && isPerDiemOrTravel && occurrences === wantedShifts) {
		return NurseGroup.PerDiemOrTravelFullyBooked;
	}

	// OverBooked
	if (!isHasAdjacentShifts && isFullTimeOrPartTime && occurrences > wantedShifts) {
		return NurseGroup.FullTimeOrPartTimeOverBooked;
	}

	if (!isHasAdjacentShifts && isFloatPool && occurrences > wantedShifts) {
		return NurseGroup.FloatPoolOverBooked;
	}

	if (!isHasAdjacentShifts && isPerDiemOrTravel && occurrences > wantedShifts) {
		return NurseGroup.PerDiemOrTravelOverBooked;
	}

	// AdjacentShift
	if (isHasAdjacentShifts) {
		return NurseGroup.AdjacentShift;
	}

	// Default case
	return NurseGroup.AdjacentShift;
};

function getNursesPreferSet(nurses: Nurse[], day: Day, shiftNo: number, calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"]) {
	return nursesWithSpecificPreference(nurses, day, shiftNo, Preferences.Prefer, calculateShiftTimes);
}

function getNursesNotPreferSet(nurses: Nurse[], day: Day, shiftNo: number, calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"]) {
	return nursesWithSpecificPreference(nurses, day, shiftNo, Preferences.Avoid, calculateShiftTimes);
}

/** The nurses with a `type` date preference covering the shift. */
const nursesWithSpecificPreference = (
	nurses: Nurse[],
	day: Day,
	shiftNo: number,
	type: Preferences,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
): Set<number> =>
	new Set(
		nurses
			.filter((nurse) => {
				if (!nurse.employeeSpecificPreferences.length) return false;
				const shiftTimes = calculateShiftTimes(day, shiftNo, nurse.role);
				return nurse.employeeSpecificPreferences.some((pref) => pref.type === type && isShiftInTime(shiftTimes, pref));
			})
			.map((nurse) => nurse.id),
	);

const sortInnGroup = (
	nurses: Nurse[],
	group: NurseGroup,
	nurseOccurrences: Record<number, number>,
	nurseOccurrencesWeekend: Record<number, number> | undefined,
	hospitalData: ScheduleData,
	shiftNo: number,
	weekIndex: number,
	dayNo: number,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
): Nurse[] => {
	const shiftType = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.type;
	const day = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayNo];
	const weekDay = day?.date.toFormat("cccc");
	const isWeekend =
		(shiftType === ShiftType.Day && (weekDay === "Saturday" || weekDay === "Sunday")) ||
		(shiftType === ShiftType.Night && (weekDay === "Friday" || weekDay === "Saturday" || weekDay === "Sunday"));

	const nursesPreferSet: Set<number> = getNursesPreferSet(nurses, day, shiftNo, calculateShiftTimes);
	const nursesNotPreferSet: Set<number> = getNursesNotPreferSet(nurses, day, shiftNo, calculateShiftTimes);

	return sortInGroupInternal(
		group,
		nurses,
		nurseOccurrences,
		nurseOccurrencesWeekend,
		nursesPreferSet,
		nursesNotPreferSet,
		isWeekend,
		weekIndex,
		shiftNo,
		dayNo,
		hospitalData,
	);
};

export const sortByWeekend = (a: Nurse, b: Nurse, nurseOccurrencesWeekend: Record<number, number> | undefined, isWeekend: boolean) => {
	if (nurseOccurrencesWeekend !== undefined && isWeekend) {
		const getRequiredShifts = (nurse: Nurse) => {
			if (nurse.employmentType === EmploymentType.PartTime || nurse.employmentType === EmploymentType.PerDiem) {
				return 3;
			}
			return 4; // 4 for Day/Night shift
		};

		const aRequiredShifts = getRequiredShifts(a);
		const bRequiredShifts = getRequiredShifts(b);

		// Check if either nurse is doing more than required weekend shifts (+1 because we count this shift also)
		const aExcessShifts = Math.max(0, nurseOccurrencesWeekend[a.id] - aRequiredShifts) + 1;
		const bExcessShifts = Math.max(0, nurseOccurrencesWeekend[b.id] - bRequiredShifts) + 1;

		// If one nurse is doing excess shifts and the other isn't, prioritize the one without excess shifts
		// If both or neither are doing excess shifts, continue with the existing logic
		if (aExcessShifts > 0 && bExcessShifts === 0) return 1;
		if (bExcessShifts > 0 && aExcessShifts === 0) return -1;

		const aOccurrences = nurseOccurrencesWeekend[a.id];
		const bOccurrences = nurseOccurrencesWeekend[b.id];

		const aRemainingRatio = aOccurrences / aRequiredShifts;
		const bRemainingRatio = bOccurrences / bRequiredShifts;
		if (aRemainingRatio !== bRemainingRatio) {
			if (aRemainingRatio > bRemainingRatio) return 1;
			if (bRemainingRatio > aRemainingRatio) return -1;
		}

		// Sort by weekend schedule flexibility in case the ratio is the same
		const compareWeekendScheduleFlexibility = (a: Nurse, b: Nurse) => {
			if (a.weekendShiftFlexibility && !b.weekendShiftFlexibility) return -1;
			if (!a.weekendShiftFlexibility && b.weekendShiftFlexibility) return 1;
			return 0;
		};

		const weekendFlexibilityComparison = compareWeekendScheduleFlexibility(a, b);
		if (weekendFlexibilityComparison !== 0) {
			return weekendFlexibilityComparison;
		}
	}
	return null;
};

export const checkMoreThanAllowedConsecutiveShifts = (
	nurseToCheck: Nurse,
	consecutiveAllowedShifts: number | undefined,
	weekIndex: number,
	shiftNo: number,
	dayNo: number,
	hospitalData: ScheduleData,
	ignoreDay: number | undefined = undefined,
) => {
	if (consecutiveAllowedShifts === undefined) {
		consecutiveAllowedShifts = MAX_CONSECUTIVE_ALLOWED_SHIFTS;
	}
	const totalWeeks = hospitalData.weeks.length;
	const totalDays = hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.length;

	// Helper function to get the correct shift
	const getShift = (weekIdx: number, dayIdx: number) => {
		if (weekIdx < 0 || weekIdx >= totalWeeks) return null;
		if (dayIdx < 0 || dayIdx >= totalDays) return null;
		return hospitalData.weeks[weekIdx].weeklyShifts[shiftNo].days[dayIdx];
	};

	// Check for consecutive shifts
	let consecutiveCount = 1;
	let dayBefore = dayNo - 1;
	let weekBefore = weekIndex;
	let dayAfter = dayNo + 1;
	let weekAfter = weekIndex;

	// Count consecutive shifts before
	while (consecutiveCount <= consecutiveAllowedShifts) {
		if (dayBefore < 0) {
			weekBefore--;
			if (weekBefore < 0) break;
			dayBefore = totalDays - 1;
		}
		if (ignoreDay !== undefined && dayBefore === ignoreDay) break;
		const shift = getShift(weekBefore, dayBefore);
		if (!shift || !shift.nurses.find((nurse) => nurse.nurseId === nurseToCheck.id)) break;
		consecutiveCount++;
		dayBefore--;
	}

	// Count consecutive shifts after
	while (consecutiveCount <= consecutiveAllowedShifts) {
		if (dayAfter >= totalDays) {
			weekAfter++;
			if (weekAfter >= totalWeeks) break;
			dayAfter = 0;
		}
		if (ignoreDay !== undefined && dayAfter === ignoreDay) break;
		const shift = getShift(weekAfter, dayAfter);
		if (!shift || !shift.nurses.find((nurse) => nurse.nurseId === nurseToCheck.id)) break;
		consecutiveCount++;
		dayAfter++;
	}
	return consecutiveCount > consecutiveAllowedShifts;
};

const compareShiftConstraints = (a: Nurse, b: Nurse, weekIndex: number, shiftNo: number, dayNo: number, hospitalData: ScheduleData) => {
	// -1 means a sorted before b, 1 means a sorted after b, 0 means no difference
	if (a.shiftConstraints?.consecutiveAllowedShifts !== undefined && b.shiftConstraints?.consecutiveAllowedShifts !== undefined) {
		const consecutiveAllowedShiftsA: number | undefined = a.shiftConstraints.consecutiveAllowedShifts;
		const consecutiveAllowedShiftsB: number | undefined = b.shiftConstraints.consecutiveAllowedShifts;

		const exceedsConsecutiveA = checkMoreThanAllowedConsecutiveShifts(a, consecutiveAllowedShiftsA, weekIndex, shiftNo, dayNo, hospitalData);
		const exceedsConsecutiveB = checkMoreThanAllowedConsecutiveShifts(b, consecutiveAllowedShiftsB, weekIndex, shiftNo, dayNo, hospitalData);

		if (exceedsConsecutiveA && !exceedsConsecutiveB) {
			return 1;
		}
		if (!exceedsConsecutiveA && exceedsConsecutiveB) {
			return -1;
		}
		return 0;
	}
	if (a?.shiftConstraints?.consecutiveAllowedShifts !== undefined && b?.shiftConstraints?.consecutiveAllowedShifts === undefined) {
		const consecutiveAllowedShifts: number | undefined = a.shiftConstraints.consecutiveAllowedShifts;
		// a can have count consecutiveShifts in a row
		if (checkMoreThanAllowedConsecutiveShifts(a, consecutiveAllowedShifts, weekIndex, shiftNo, dayNo, hospitalData)) {
			return 1;
		}
		return -1;
	}
	if (a.shiftConstraints?.consecutiveAllowedShifts === undefined && b.shiftConstraints?.consecutiveAllowedShifts !== undefined) {
		const consecutiveAllowedShifts: number | undefined = b.shiftConstraints.consecutiveAllowedShifts;
		// b can have count consecutiveShifts in a row
		if (checkMoreThanAllowedConsecutiveShifts(b, consecutiveAllowedShifts, weekIndex, shiftNo, dayNo, hospitalData)) {
			return -1;
		}
		return 1;
	}
	return 0;
};

export const compareNurseMoveCount = (hospitalData: ScheduleData, a: Nurse, b: Nurse, weekIndex: number) => {
	const moveA = getNurseChanges(hospitalData, a.id);
	const moveB = getNurseChanges(hospitalData, b.id);
	const moveCountA = Math.max(moveA.additions.length, moveA.removals.length);
	const moveCountB = Math.max(moveB.additions.length, moveB.removals.length);

	if (moveCountA !== moveCountB) {
		return moveCountA - moveCountB;
	}
	const moveWeekIndexA = getNurseChanges(hospitalData, a.id, weekIndex);
	const moveWeekIndexB = getNurseChanges(hospitalData, b.id, weekIndex);

	const moveCountWeekIndexA = Math.max(moveWeekIndexA.additions.length, moveWeekIndexA.removals.length);
	const moveCountWeekIndexB = Math.max(moveWeekIndexB.additions.length, moveWeekIndexB.removals.length);

	if (moveCountWeekIndexA !== moveCountWeekIndexB) {
		return moveCountWeekIndexA - moveCountWeekIndexB;
	}
	return 0;
};

const sortRgularAvailable = (
	nurses: Nurse[],
	nurseOccurrences: Record<number, number>,
	nurseOccurrencesWeekend: Record<number, number> | undefined,
	nursesPreferSet: Set<number>,
	nursesNotPreferSet: Set<number>,
	isWeekend: boolean,
	weekIndex: number,
	shiftNo: number,
	dayNo: number,
	hospitalData: ScheduleData,
) => {
	// Sort by the ratio of the number of occurrences to the wanted shifts(lower ratio higher score),
	//  if the ratio is the same, sort by the number of wanted shifts (higher number sort higher) and if that is the same,
	//   sort by the score cost ratio (higher number sort higher)
	return nurses.sort((a, b) => {
		const preferenceComparison = comparePreferences(a, b, nursesPreferSet, nursesNotPreferSet);
		if (preferenceComparison !== 0) {
			return preferenceComparison;
		}
		const weekendComparison = sortByWeekend(a, b, nurseOccurrencesWeekend, isWeekend);
		if (weekendComparison !== null) {
			return weekendComparison;
		}

		if (compareNurseMoveCount(hospitalData, a, b, weekIndex) !== 0) {
			return compareNurseMoveCount(hospitalData, a, b, weekIndex);
		}

		const shiftConstraintsComparison = compareShiftConstraints(a, b, weekIndex, shiftNo, dayNo, hospitalData);
		if (shiftConstraintsComparison !== 0) {
			return shiftConstraintsComparison;
		}

		const aRatio = nurseOccurrences[a.id] / a.allowedShifts;
		const bRatio = nurseOccurrences[b.id] / b.allowedShifts;
		if (aRatio !== bRatio) {
			return aRatio - bRatio;
		}
		if (a.allowedShifts !== b.allowedShifts) {
			return b.allowedShifts - a.allowedShifts;
		}

		if (scoreCostRatio(a) !== scoreCostRatio(b)) {
			return scoreCostRatio(b) - scoreCostRatio(a);
		}

		// If all other comparisons are equal, sort by nurse id in ascending order
		return a.id - b.id;
	});
};

const sortNursesByOverBookedAmountAndScoreCostRatio = (
	nurses: Nurse[],
	nurseOccurrences: Record<number, number>,
	nurseOccurrencesWeekend: Record<number, number> | undefined,
	nursesPreferSet: Set<number>,
	nursesNotPreferSet: Set<number>,
	isWeekend: boolean,
	weekIndex: number,
	shiftNo: number,
	dayNo: number,
	hospitalData: ScheduleData,
) => {
	// Sort by the amount of extra shifts (occurrences - wanted shifts) (lower number higher in sort), if the ratio is the same, sort by the score cost ratio (higher number sort higher)
	return nurses.sort((a, b) => {
		const preferenceComparison = comparePreferences(a, b, nursesPreferSet, nursesNotPreferSet);
		if (preferenceComparison !== 0) {
			return preferenceComparison;
		}
		const weekendComparison = sortByWeekend(a, b, nurseOccurrencesWeekend, isWeekend);
		if (weekendComparison !== null) {
			return weekendComparison;
		}

		if (compareNurseMoveCount(hospitalData, a, b, weekIndex) !== 0) {
			return compareNurseMoveCount(hospitalData, a, b, weekIndex);
		}

		const shiftConstraintsComparison = compareShiftConstraints(a, b, weekIndex, shiftNo, dayNo, hospitalData);
		if (shiftConstraintsComparison !== 0) {
			return shiftConstraintsComparison;
		}

		const aOverBooked = nurseOccurrences[a.id] - a.allowedShifts;
		const bOverBooked = nurseOccurrences[b.id] - b.allowedShifts;
		if (aOverBooked !== bOverBooked) {
			return aOverBooked - bOverBooked;
		}

		if (scoreCostRatio(a) !== scoreCostRatio(b)) {
			return scoreCostRatio(b) - scoreCostRatio(a);
		}

		// If all other comparisons are equal, sort by nurse id in ascending order
		return a.id - b.id;
	});
};

const isFullTimeOrPartTimeNurse = (nurse: any) => nurse.employmentType === EmploymentType.FullTime || nurse.employmentType === EmploymentType.PartTime;
const isPerDiemOrTravelNurse = (nurse: any) => nurse.employmentType === EmploymentType.PerDiem || nurse.employmentType === EmploymentType.Travel;

export const comparePreferences = (a: Nurse, b: Nurse, nursesPreferSet: Set<number>, nursesNotPreferSet: Set<number>): number => {
	// Check not prefer set
	if (nursesNotPreferSet.has(a.id)) {
		if (!nursesNotPreferSet.has(b.id)) {
			return 1;
		}
	} else if (nursesNotPreferSet.has(b.id)) {
		return -1;
	}

	// Check prefer set
	if (nursesPreferSet.has(a.id)) {
		if (!nursesPreferSet.has(b.id)) {
			return -1;
		}
	} else if (nursesPreferSet.has(b.id)) {
		return 1;
	}

	return 0; // No preference difference
};

const sortByScoreCostRatio = (
	nurses: Nurse[],
	nurseOccurrences: Record<number, number>,
	nurseOccurrencesWeekend: Record<number, number> | undefined,
	nursesPreferSet: Set<number>,
	nursesNotPreferSet: Set<number>,
	isWeekend: boolean,
	weekIndex: number,
	shiftNo: number,
	dayNo: number,
	hospitalData: ScheduleData,
) => {
	// create function of sorting both preferSet and not preferSet
	return nurses.sort((a, b) => {
		const preferenceComparison = comparePreferences(a, b, nursesPreferSet, nursesNotPreferSet);
		if (preferenceComparison !== 0) {
			return preferenceComparison;
		}
		const weekendComparison = sortByWeekend(a, b, nurseOccurrencesWeekend, isWeekend);
		if (weekendComparison !== null) {
			return weekendComparison;
		}

		if (compareNurseMoveCount(hospitalData, a, b, weekIndex) !== 0) {
			return compareNurseMoveCount(hospitalData, a, b, weekIndex);
		}

		const shiftConstraintsComparison = compareShiftConstraints(a, b, weekIndex, shiftNo, dayNo, hospitalData);
		if (shiftConstraintsComparison !== 0) {
			return shiftConstraintsComparison;
		}

		const aScoreCost = scoreCostRatio(a);
		const bScoreCost = scoreCostRatio(b);
		if (aScoreCost !== bScoreCost) {
			return bScoreCost - aScoreCost;
		}

		// If all other comparisons are equal, sort by nurse id in ascending order
		return a.id - b.id;
	});
};

function sortInGroupInternal(
	group: NurseGroup,
	nurses: Nurse[],
	nurseOccurrences: Record<number, number>,
	nurseOccurrencesWeekend: Record<number, number> | undefined,
	nursesPreferSet: Set<number>,
	nursesNotPreferSet: Set<number>,
	isWeekend: boolean,
	weekIndex: number,
	shiftNo: number,
	dayNo: number,
	hospitalData: ScheduleData,
) {
	switch (group) {
		case NurseGroup.FullTimeOrPartTimeAvailable || NurseGroup.FloatPoolAvailable || NurseGroup.FullTimeOrPartTimeFullyBooked || NurseGroup.FloatPoolFullyBooked:
			return sortRgularAvailable(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		case NurseGroup.PerDiemOrTravelAvailable:
			return sortByScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		case NurseGroup.FullTimeOrPartTimeFullyBooked || NurseGroup.FloatPoolFullyBooked:
			return sortByScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		case NurseGroup.PerDiemOrTravelFullyBooked:
			return sortByScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		case NurseGroup.FullTimeOrPartTimeOverBooked || NurseGroup.FloatPoolOverBooked:
			return sortNursesByOverBookedAmountAndScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		case NurseGroup.PerDiemOrTravelOverBooked:
			return sortNursesByOverBookedAmountAndScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		case NurseGroup.ShiftUnavailable:
			return sortByScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		case NurseGroup.AdjacentShift:
			return sortByScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
		default:
			return sortByScoreCostRatio(
				nurses,
				nurseOccurrences,
				nurseOccurrencesWeekend,
				nursesPreferSet,
				nursesNotPreferSet,
				isWeekend,
				weekIndex,
				shiftNo,
				dayNo,
				hospitalData,
			);
	}
}

export const isShiftInTime = (shiftTimes: { startTime: DateTime; endTime: DateTime }, pref: SpecificPreference): boolean =>
	pref.start_date <= shiftTimes.startTime && shiftTimes.startTime < pref.end_date;

export function isCannotWork(
	nurse: Nurse,
	hospitalData: ScheduleData,
	weekIndex: number,
	shiftNo: number,
	dayNo: number,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
) {
	// check specific preferences for this day

	const specificPreferencePrefer = nurse.employeeSpecificPreferences?.find(
		(pref) =>
			pref.type === Preferences.Prefer &&
			isShiftInTime(calculateShiftTimes(hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo], shiftNo, nurse.role), pref),
	);
	if (specificPreferencePrefer) {
		return false;
	}

	// check specific date preferences
	const specificPreferenceAvoid = nurse.employeeSpecificPreferences?.find(
		(pref) =>
			pref.type === Preferences.Avoid &&
			isShiftInTime(calculateShiftTimes(hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo], shiftNo, nurse.role), pref),
	);
	return !!specificPreferenceAvoid;
}

const scoreCostRatio = (nurse: any) => {
	// TODO uria - for annual, we get shift salary and not hourly salary
	return nurse.wage > 0 ? nurse.reliabilityScore / getWage(nurse) : Number.MAX_SAFE_INTEGER;
};

export function updateNurseCount(
	nurseCount: Map<number, Array<ShiftDetails>>,
	nurse_id: number,
	weekIndex: number,
	shiftNo: number,
	dayIndex: number,
	date: DateTime,
	startTime: DateTime,
	endTime: DateTime,
) {
	// Track the nurse shifts
	if (nurseCount.has(nurse_id)) {
		nurseCount.get(nurse_id)?.push({
			weekIndex: weekIndex,
			shiftNo: shiftNo,
			dayNo: dayIndex,
			date: date,
			startTime: startTime,
			endTime: endTime,
		});
	} else {
		nurseCount.set(nurse_id, [
			{
				weekIndex: weekIndex,
				shiftNo: shiftNo,
				dayNo: dayIndex,
				date: date,
				startTime: startTime,
				endTime: endTime,
			},
		]);
	}
}

type TrainerShiftInfo = { shiftNo: number; dayIndex: number };
function getNurseStaffingIssues(
	context: StaffingContext,
	role: RoleKey,
	departmentNurseTypes: NurseFullRole[],
	requests: (OpenShiftRequest | ReplaceShiftRequest)[],
	timezone: string,
	isLowry: boolean,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
	jobManagementEnabled = false,
) {
	// will present issues regarding the nurses:
	// 1. Back-to-back shifts
	// 2. A part-time nurse is working beyond the required percentage of hours.
	// 3. Suggest replacing regular staff (full-time/part-time) instead of relying on the float pool
	// 4. Suggest replacing with full-time, part-time, or float pool staff instead of using external resources
	// 5. Nurse is working more than the required
	const nurseIssues: StaffingIssue[] = [];
	const nurseCount: Map<number, Array<ShiftDetails>> = new Map();
	const trainerShifts: Map<string, TrainerShiftInfo> = new Map();

	for (let shiftNo = 0; shiftNo < context.currWeek.weeklyShifts.length; shiftNo++) {
		const weeklyShifts: Shift = context.currWeek.weeklyShifts[shiftNo];
		for (let dayIndex = 0; dayIndex < weeklyShifts.days.length; dayIndex++) {
			const day: Day = weeklyShifts.days[dayIndex];
			for (const nurse of day.nurses) {
				if (nurse.isPendingToDelete) continue;
				const nurseData: Nurse | undefined = context.nurseMap.get(nurse.nurseId);
				// remove it after RN is working
				if (nurseData === undefined || nurseData.role !== rolesMap[role]) continue;

				updateNurseCount(nurseCount, nurse.nurseId, context.weekIndex, shiftNo, dayIndex, day.date, nurse.startTime, nurse.endTime);
				if (nurseData.trainingStatus === NurseTrainingStatus.Trainer) {
					trainerShifts.set(`${shiftNo}-${dayIndex}`, { shiftNo, dayIndex });
				}
			}
		}
	}

	// checking if nurse working more than the required
	// overtime nurse
	handleOverTimeNurse(context, nurseCount, nurseIssues, departmentNurseTypes, timezone, isLowry, calculateShiftTimes, false, jobManagementEnabled);

	addTraineeIssues(context, role, trainerShifts, nurseIssues, calculateShiftTimes);
	// Those recommendations are for posting shifts and so, they are not relevant for demo.
	// Those recommendations are buggy, removed for now
	// if (context.chosenDepartment.department_id != DepartmentNames.InHouseHospitalDemo && context.chosenDepartment.department_id != DepartmentNames.InHouseSNF) {
	// 	getRepalceNurseForLowerPredictedPriceIssues(context, nurseIssues, requests, timezone, isLowry);
	// }

	return nurseIssues;
}

function addTraineeIssues(
	context: StaffingContext,
	role: NurseRole,
	trainerShifts: Map<string, TrainerShiftInfo>,
	nurseIssues: StaffingIssue[],
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
) {
	for (let shiftNo = 0; shiftNo < context.currWeek.weeklyShifts.length; shiftNo++) {
		const weeklyShifts: Shift = context.currWeek.weeklyShifts[shiftNo];
		for (let dayIndex = 0; dayIndex < weeklyShifts.days.length; dayIndex++) {
			const day: Day = weeklyShifts.days[dayIndex];
			for (const nurse of day.nurses) {
				if (nurse.isPendingToDelete) continue;
				const nurseData: Nurse | undefined = context.nurseMap.get(nurse.nurseId);
				// remove it after RN is working
				if (nurseData === undefined || nurseData.role !== rolesMap[role]) continue;

				if (
					nurseData.trainingStatus === NurseTrainingStatus.Trainee &&
					!day.nurses.some((n) => !n.isPendingToDelete && context.nurseMap.get(n.nurseId)?.trainingStatus === NurseTrainingStatus.Trainer)
				) {
					const suggestions: Suggestion[] = [];
					// If shift is understaff & has available trainer -> Add the trainer
					if (isOverstaffed(day)) {
						suggestions.push({
							action: SuggestionAction.Remove,
							nurseId: nurse.nurseId,
							shiftNo: shiftNo,
							dayNo: dayIndex,
							weekIndex: context.weekIndex,
							displayNurse: true,
						});
					} else if (isUnderstaffed(day)) {
						for (const newNurse of context.hospitalData.nurses) {
							if (newNurse.trainingStatus !== NurseTrainingStatus.Trainer) {
								continue;
							}

							const groupIndex = determineGroupIndex(
								newNurse,
								context.hospitalData,
								shiftNo,
								context.weekIndex,
								dayIndex,
								context.nurseOcuurences,
								calculateShiftTimes,
							);
							if (groupIndex > NurseGroup.PerDiemOrTravelAvailable) {
								continue;
							}

							suggestions.push({
								action: SuggestionAction.Add,
								nurseId: newNurse.id,
								shiftNo: shiftNo,
								dayNo: dayIndex,
								weekIndex: context.weekIndex,
								displayNurse: true,
							});
						}
					}
					if (suggestions.length === 0) {
						const optionalSwapNurses = sortNurses(
							context.hospitalData,
							shiftNo,
							context.weekIndex,
							dayIndex,
							context.nurseOcuurences,
							context.calculateShiftTimes,
							false,
							role,
							false,
						);
						for (const nurseToSwap of optionalSwapNurses) {
							if (nurseToSwap.trainingStatus === NurseTrainingStatus.Trainer) {
								continue;
							}
							const groupIndex = determineGroupIndex(
								nurseToSwap,
								context.hospitalData,
								shiftNo,
								context.weekIndex,
								dayIndex,
								context.nurseOcuurences,
								context.calculateShiftTimes,
							);
							if (groupIndex > NurseGroup.PerDiemOrTravelFullyBooked) {
								break;
							}
							for (const shift of trainerShifts.values()) {
								if (
									context.currWeek.weeklyShifts[shift.shiftNo].days[shift.dayIndex].nurses.some((n) => n.nurseId === nurseToSwap.id) &&
									!context.currWeek.weeklyShifts[shift.shiftNo].days[shift.dayIndex].nurses.some((n) => n.nurseId === nurseData.id)
								) {
									const groupIndex = determineGroupIndex(
										nurseData,
										context.hospitalData,
										shift.shiftNo,
										context.weekIndex,
										shift.dayIndex,
										context.nurseOcuurences,
										context.calculateShiftTimes,
									);
									if (groupIndex > NurseGroup.PerDiemOrTravelFullyBooked) {
										continue;
									}
									suggestions.push({
										action: SuggestionAction.Swap,
										nurseId: nurseData.id,
										shiftNo: shift.shiftNo,
										dayNo: shift.dayIndex,
										weekIndex: context.weekIndex,
										otherNurseId: nurseToSwap.id,
										otherShiftNo: shiftNo,
										otherDayNo: dayIndex,
									});
								}
							}
						}
					}
					const shiftTimes = calculateShiftTimes(day, shiftNo, nurseData?.role);
					const trainee_issue: StaffingIssue = {
						suggestions: suggestions.slice(0, 3),
						issueType: IssueType.Nurse,
						priority: IssuePriority.Important,
						shiftTimes: {
							start: shiftTimes.startTime,
							end: shiftTimes.endTime,
							date: day.date,
							shiftType: weeklyShifts.type as ShiftType,
						},
						title: [
							{
								type: "bold",
								text: `${formatPersonFullName(nurseData, context.lastNameFirst)} `,
							},
							{ type: "normal", text: "is a " },
							{ type: "bold", text: "trainee " },
							{ type: "normal", text: "that works on " },
							{
								type: "bold",
								text: `${day.date.toFormat("cccc")} ${weeklyShifts.type} without a preceptor`,
							},
						],
					};

					nurseIssues.push(trainee_issue);
				}
			}
		}
	}
}

export function handleOverTimeNurse(
	context: StaffingContext,
	nurseCount: Map<number, Array<ShiftDetails>>,
	nurseIssues: StaffingIssue[],
	departmentNurseTypes: NurseFullRole[],
	timezone: string,
	isLowry: boolean,
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
	isAutoScheduler = false,
	jobManagementEnabled = false,
) {
	const overstaffNurses = Array.from(nurseCount.entries()).filter(([nurseID, shifts]) => {
		const nurse = context.nurseMap.get(nurseID);
		if (isAutoScheduler && (nurse?.isShiftManager || nurse?.employmentType === EmploymentType.PerDiem)) {
			return false;
		}
		const allowedShifts: number = nurse?.allowedShifts ?? 0;
		return shifts.length > allowedShifts;
	});
	const weekData = context.currWeek;
	for (const [nurseID, shifts] of overstaffNurses) {
		const fullNurse = context.nurseMap.get(nurseID);
		if (!fullNurse) continue;
		const suggestions: Suggestion[] = [];
		for (const shift of shifts.reverse()) {
			// We are using reverese to see the last days of the week first since it's better when demoing overtime
			const census = getCensus(weekData, shift.dayNo);
			const wantedHours = getSnfWantedNursesHours(weekData, shift.dayNo, context.chosenDepartment);
			const targetHppd = Number((wantedHours / census).toFixed(2));
			const day = weekData.weeklyShifts[shift.shiftNo].days[shift.dayNo];
			const { totalStaff, totalHours } = getTotalHours(
				context.chosenDepartment.department_id,
				context.currWeek.weeklyShifts,
				shift.dayNo,
				false,
				true,
				undefined,
				[nurseID],
				undefined,
				context.chosenDepartment.breakRulesConfig ?? [],
				jobManagementEnabled,
				Array.from(context.nurseMap.values()),
				trainingPpdCountingOf(context.chosenDepartment), // HPPD counts the training statuses the department's switches count
				false, // and excludes ppd-excluded staff
			);
			if (isOverstaffed(day)) {
				const newTargetHppd = census > 0 ? Number((totalHours / census).toFixed(2)) : 0;
				if (newTargetHppd >= targetHppd) {
					suggestions.push({
						action: SuggestionAction.Remove,
						nurseId: nurseID,
						dayNo: shift.dayNo,
						shiftNo: shift.shiftNo,
						weekIndex: context.weekIndex,
					});
				}
			} else {
				const replacementNurses = sortNurses(
					context.hospitalData,
					shift.shiftNo,
					context.weekIndex,
					shift.dayNo,
					context.nurseOcuurences,
					context.calculateShiftTimes,
					false,
					inverseRolesMap[fullNurse.role],
				);
				if (!replacementNurses.length) continue;

				if (!isNeedReplace(context, fullNurse, replacementNurses[0], shift.shiftNo, shift.dayNo)) {
					continue;
				}
				suggestions.push({
					action: SuggestionAction.Replace,
					nurseId: nurseID,
					dayNo: shift.dayNo,
					shiftNo: shift.shiftNo,
					weekIndex: context.weekIndex,
					otherNurseId: replacementNurses[0].id,
				});
			}
		}
		if (suggestions.length === 0) continue;
		let nurseIssue: StaffingIssue;
		// optional case: if it's a part time nurse working more than the required percentage but less than 100%
		let isOptionalCase = false;

		if (fullNurse?.employmentType === EmploymentType.PartTime) {
			const allowedShifts: number = fullNurse?.allowedShifts ?? 0;
			isOptionalCase = !(shifts.length > allowedShifts);
		}

		if (!isOptionalCase) {
			const shift = context.currWeek.weeklyShifts[suggestions[0].shiftNo];
			const shiftTimes = calculateShiftTimes(shift.days[suggestions[0].dayNo], suggestions[0].shiftNo, fullNurse?.role);
			nurseIssue = {
				suggestions: suggestions,
				issueType: IssueType.Nurse,
				shiftTimes: {
					start: shiftTimes.startTime,
					end: shiftTimes.endTime,
					date: shift.days[suggestions[0].dayNo].date,
					shiftType: shift.type as ShiftType,
				},
				priority: IssuePriority.Important,
				title: [
					{
						type: "bold",
						text: `${formatPersonFullName(fullNurse, context.lastNameFirst)} `,
					},
					{ type: "normal", text: "is in " },
					{ type: "bold", text: "overtime, " },
					{
						type: "normal",
						text:
							suggestions[0].action === SuggestionAction.Replace
								? "consider replacing them with someone else for this shift"
								: "consider removing them from one of the shifts",
					},
				],
			};
		} else {
			const shift = context.currWeek.weeklyShifts[suggestions[0].shiftNo];
			const shiftTimes = calculateShiftTimes(shift.days[suggestions[0].dayNo], suggestions[0].shiftNo, fullNurse?.role);
			nurseIssue = {
				suggestions: suggestions,
				issueType: IssueType.Nurse,
				priority: IssuePriority.Optional,
				shiftTimes: {
					start: shiftTimes.startTime,
					end: shiftTimes.endTime,
					date: shift.days[suggestions[0].dayNo].date,
					shiftType: shift.type as ShiftType,
				},
				title: [
					{
						type: "bold",
						text: `${formatPersonFullName(fullNurse, context.lastNameFirst)} `,
					},
					{ type: "normal", text: "is a " },
					{ type: "bold", text: "part time " },
					{ type: "normal", text: "with " },
					{ type: "bold", text: "more shifts than expected, " },
					{
						type: "normal",
						text:
							suggestions[0].action === SuggestionAction.Replace
								? "consider replacing them with someone else for this shift"
								: "consider removing them from one of the shifts",
					},
				],
			};
		}
		nurseIssues.unshift(nurseIssue);
	}
}

function isNeedReplace(context: StaffingContext, currentNurse: Nurse, suggestedNurse: Nurse, shiftIndex: number, dayIndex: number) {
	const suggestedNurseGroup = determineGroupIndex(
		suggestedNurse,
		context.hospitalData,
		shiftIndex,
		context.weekIndex,
		dayIndex,
		context.nurseOcuurences,
		context.calculateShiftTimes,
		1,
	);
	const currentNurseGroup = determineGroupIndex(
		currentNurse,
		context.hospitalData,
		shiftIndex,
		context.weekIndex,
		dayIndex,
		context.nurseOcuurences,
		context.calculateShiftTimes,
	);

	return !(currentNurseGroup === null || suggestedNurseGroup === null || suggestedNurseGroup >= currentNurseGroup);
}

function snfChooseAddShift(currWeek: WeekData, dayIndex: number) {
	// Ideal is to have the same number of nurses in each shift, prefer the first shift
	let shiftNo = 0;
	if (currWeek.weeklyShifts[0].days[dayIndex].nurses.length > currWeek.weeklyShifts[1].days[dayIndex].nurses.length) {
		shiftNo = 1;
	}
	return shiftNo;
}

function snfChooseRemoveShift(currWeek: WeekData, dayIndex: number) {
	// Ideal is to have the same number of nurses in each shift, prefer the last
	let shiftNo = 1;
	if (currWeek.weeklyShifts[0].days[dayIndex].nurses.length > currWeek.weeklyShifts[1].days[dayIndex].nurses.length) {
		shiftNo = 0;
	}
	return shiftNo;
}

function createSnfUnderstaffedIssue(
	context: StaffingContext,
	census: number,
	dayIndex: number,
	date: DateTime,
	cnaRatioTarget: number,
	nursesRatioTarget: number,
	nonManagerNursesHoursData: any,
	departmentNurseTypes: NurseFullRole[],
	timezone: string,
	isLowry: boolean,
) {
	const suggestions: Suggestion[] = [];
	// Since we are not recomeding to add / remove shift manager, we don't count them towards this ratio
	// const cmaPPN = census / nonManagerNursesHoursData.CMA.numOfNurses;
	const cnaPPN = census / nonManagerNursesHoursData.CNA.numOfNurses;
	// const nasPPN = census / nonManagerNursesHoursData.NAS.numOfNurses;
	// const mtPPN = census / nonManagerNursesHoursData.MT.numOfNurses;
	// const raPPN = census / nonManagerNursesHoursData.RA.numOfNurses;
	// const daPPN = census / nonManagerNursesHoursData.DA.numOfNurses;
	// const cookPPN = census / nonManagerNursesHoursData.CK.numOfNurses;
	// const rdPPN = census / nonManagerNursesHoursData.RD.numOfNurses;
	// const caPPN = census / nonManagerNursesHoursData.CA.numOfNurses;
	const nursePPN = census / (nonManagerNursesHoursData.RN.numOfNurses + nonManagerNursesHoursData.LPN.numOfNurses + nonManagerNursesHoursData.LVN.numOfNurses);

	let roleToAdd = [NurseRole.CNA];
	if (cnaPPN >= cnaRatioTarget && nursePPN < nursesRatioTarget) {
		// Deafult is CNA, if CNA is above target and Nurses below, use Nurses
		roleToAdd = [
			NurseRole.RN,
			...(departmentNurseTypes.includes(NurseFullRole.LPN) ? [NurseRole.LPN] : []),
			...(departmentNurseTypes.includes(NurseFullRole.LVN) ? [NurseRole.LVN] : []),
		];
	}

	const shiftNo = snfChooseAddShift(context.currWeek, dayIndex);
	const nursesToAdd = roleToAdd
		.flatMap((role) => {
			return sortNurses(context.hospitalData, shiftNo, context.weekIndex, dayIndex, context.nurseOcuurences, context.calculateShiftTimes, false, role);
		})
		.filter((nurse) => !nurse.isShiftManager);

	for (let nurseIndex = 0; nurseIndex < nursesToAdd.length; nurseIndex++) {
		suggestions.push({
			action: SuggestionAction.Add,
			nurseId: nursesToAdd[nurseIndex].id,
			shiftNo: shiftNo,
			dayNo: dayIndex,
			weekIndex: context.weekIndex,
		});
	}

	const shiftTimes = context.calculateShiftTimes(context.currWeek.weeklyShifts[shiftNo].days[dayIndex], shiftNo, nursesToAdd[0]?.role);
	const issue: StaffingIssue = {
		suggestions: suggestions,
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		shiftTimes: {
			shiftType: context.currWeek.weeklyShifts[shiftNo].type as ShiftType,
			date: date,
			start: shiftTimes.startTime,
			end: shiftTimes.endTime,
		},
		title: [
			{ type: "bold", text: `${date.toFormat("cccc")} ` },
			{ type: "normal", text: "is " },
			{ type: "bold", text: "understaffed, " },
			{ type: "normal", text: "consider assigning additional " },
			{
				type: "bold",
				text: `${roleToAdd.includes(NurseRole.CNA) ? "CNA" : "Nurse"} `,
			},
			{
				type: "normal",
				text: `for the ${shiftNo === 0 ? "Day" : "Night"} shift`,
			},
		],
	};
	return issue;
}

function filterShiftManagerByLocation(nurses: NurseInShift[], chosenDepartment: Department) {
	const charge_locations = chosenDepartment.locations.filter((loc: DepartmentLocation) => loc.is_charge).map((loc: DepartmentLocation) => loc.location);
	return nurses.filter((nurse: NurseInShift) => nurse.location && !charge_locations.includes(nurse.location)).map((nurse: NurseInShift) => nurse.nurseId);
}

function filterRemovableNursesByHppd(
	context: StaffingContext,
	census: number,
	shiftNo: number,
	dayIndex: number,
	currentShiftTeamNurses: Nurse[],
	departmentNurseTypes: NurseFullRole[],
	jobManagementEnabled = false,
) {
	// in case of remove one of the nurses the new hppd is lower than target hppd, don't put the suggestion to remove
	const departmentId = context.chosenDepartment.department_id;
	const wantedHours = getSnfWantedNursesHours(context.currWeek, dayIndex, context.chosenDepartment);
	const targetHppd = Number((wantedHours / census).toFixed(2));
	const nursesThatCanBeRemoved: Nurse[] = [];
	for (const nurse of currentShiftTeamNurses) {
		const { totalStaff, totalHours } = getTotalHours(
			departmentId,
			context.currWeek.weeklyShifts,
			dayIndex,
			false,
			true,
			undefined,
			[nurse.id],
			undefined,
			context.chosenDepartment.breakRulesConfig ?? [],
			jobManagementEnabled,
			Array.from(context.nurseMap.values()),
			trainingPpdCountingOf(context.chosenDepartment), // HPPD counts the training statuses the department's switches count
			false, // and excludes ppd-excluded staff
		);
		const newTargetHppd = census > 0 ? Number((totalHours / census).toFixed(2)) : 0;
		// check if we remove this nurse from shift we'll still be above or equal to target hppd
		if (newTargetHppd >= targetHppd) {
			nursesThatCanBeRemoved.push(nurse);
		}
	}
	return nursesThatCanBeRemoved;
}

function createSnfOverstaffedIssue(
	context: StaffingContext,
	census: number,
	dayIndex: number,
	date: DateTime,
	cnaRatioTarget: number,
	nursesRatioTarget: number,
	nursesHoursData: any,
	departmentNurseTypes: NurseFullRole[],
	timezone: string,
	isLowry: boolean,
	jobManagementEnabled = false,
) {
	const suggestions: Suggestion[] = [];
	// Need to remove staff, decide if Nurse or CNA, and if Day or Night
	const afterRemovalCNARatio = census / (nursesHoursData.CNA.numOfNurses - 1);
	const afterRemovalNursesRatio = census / (nursesHoursData.RN.numOfNurses + nursesHoursData.LPN.numOfNurses + nursesHoursData.LVN.numOfNurses - 1);

	let roleToRemove = [
		NurseFullRole.RN,
		...(departmentNurseTypes.includes(NurseFullRole.LPN) ? [NurseFullRole.LPN] : []),
		...(departmentNurseTypes.includes(NurseFullRole.LVN) ? [NurseFullRole.LVN] : []),
	];
	if (afterRemovalNursesRatio >= nursesRatioTarget && afterRemovalCNARatio < cnaRatioTarget) {
		// We prefer to remove Nurse, if Nurse is above target and CNA is below, remove CNA
		roleToRemove = [NurseFullRole.CNA];
	}

	const shiftNo = snfChooseRemoveShift(context.currWeek, dayIndex);

	// get this shift non charge nurses, charge is nurses that are assigned to charge location and is a Nurse (RN or LPN/LVN)
	const currentShiftTeam = context.currWeek.weeklyShifts[shiftNo].days[dayIndex].nurses.filter((nurse: NurseInShift) => roleToRemove.includes(nurse.role));
	const currentShiftNonChargeTeamIds = filterShiftManagerByLocation(currentShiftTeam, context.chosenDepartment);
	const currentShiftTeamNurses: Nurse[] = context.hospitalData.nurses.filter((nurse: Nurse) => currentShiftNonChargeTeamIds.includes(nurse.id));
	const nursesThatCanBeRemoved: Nurse[] = filterRemovableNursesByHppd(
		context,
		census,
		shiftNo,
		dayIndex,
		currentShiftTeamNurses,
		departmentNurseTypes,
		jobManagementEnabled,
	);
	const nursesToRemove = sortNurses(
		context.hospitalData,
		shiftNo,
		context.weekIndex,
		dayIndex,
		context.nurseOcuurences,
		context.calculateShiftTimes,
		true,
		undefined,
		false,
		nursesThatCanBeRemoved,
	);

	if (nursesToRemove.length === 0) return null;
	for (let nurseIndex = 0; nurseIndex < nursesToRemove.length; nurseIndex++) {
		suggestions.push({
			action: SuggestionAction.Remove,
			nurseId: nursesToRemove[nurseIndex].id,
			shiftNo: shiftNo,
			dayNo: dayIndex,
			weekIndex: context.weekIndex,
		});
	}
	const shiftTimes = context.calculateShiftTimes(context.currWeek.weeklyShifts[shiftNo].days[dayIndex], shiftNo, nursesToRemove[0]?.role);
	const issue: StaffingIssue = {
		suggestions: suggestions,
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		shiftTimes: {
			shiftType: context.currWeek.weeklyShifts[shiftNo].type as ShiftType,
			date: date,
			start: shiftTimes.startTime,
			end: shiftTimes.endTime,
		},
		title: [
			{ type: "bold", text: `${date.toFormat("cccc")} ` },
			{ type: "normal", text: "is " },
			{ type: "bold", text: "overstaffed, " },
			{ type: "normal", text: "consider removing a " },
			{
				type: "bold",
				text: `${roleToRemove.includes(NurseFullRole.CNA) ? "CNA" : "Nurse"} `,
			},
			{
				type: "normal",
				text: `from the ${shiftNo === 0 ? "Day" : "Night"} shift`,
			},
		],
	};
	return issue;
}

function createRegulationComplaintIssue(context: StaffingContext, dayIndex: number, date: DateTime, roleToAdd: NurseRole[], timezone: string) {
	const suggestions: Suggestion[] = [];
	const shiftNo = snfChooseAddShift(context.currWeek, dayIndex);

	const nursesToAdd = roleToAdd
		.flatMap((role) => {
			return sortNurses(context.hospitalData, shiftNo, context.weekIndex, dayIndex, context.nurseOcuurences, context.calculateShiftTimes, false, role);
		})
		.filter((nurse) => !nurse.isShiftManager);
	for (let nurseIndex = 0; nurseIndex < nursesToAdd.length; nurseIndex++) {
		suggestions.push({
			action: SuggestionAction.Add,
			nurseId: nursesToAdd[nurseIndex].id,
			shiftNo: shiftNo,
			dayNo: dayIndex,
			weekIndex: context.weekIndex,
		});
	}
	const shiftTimes = context.calculateShiftTimes(context.currWeek.weeklyShifts[shiftNo].days[dayIndex], shiftNo, nursesToAdd[0]?.role);
	const issue: StaffingIssue = {
		suggestions: suggestions,
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		shiftTimes: {
			shiftType: context.currWeek.weeklyShifts[shiftNo].type as ShiftType,
			date: date,
			start: shiftTimes.startTime,
			end: shiftTimes.endTime,
		},
		title: [
			{ type: "bold", text: `${date.toFormat("cccc")} ` },
			{ type: "normal", text: "is" },
			{ type: "bold", text: "below required PPD target," },
			{ type: "normal", text: "consider assigning additional" },
			{
				type: "bold",
				text: `${roleToAdd.includes(NurseRole.CNA) ? "CNA" : "Nurse"} `,
			},
			{
				type: "normal",
				text: `for the ${shiftNo === 0 ? "Day" : "Night"} shift`,
			},
		],
	};
	return issue;
}

function getSnfShiftRatioTarget(chosenDepartment: Department) {
	let cnaRatioTarget = 10;
	let nursesRatioTarget = 4;

	if (chosenDepartment.department_id === DepartmentNames.CenterAtLowryMainDept) {
		cnaRatioTarget = 10 / 2;
		nursesRatioTarget = 16 / 2;
	}

	return {
		cnaRatioTarget,
		nursesRatioTarget,
	};
}

function getOverstaffedUnderstaffedIssues(
	context: StaffingContext,
	issues: StaffingIssue[],
	departmentNurseTypes: NurseFullRole[],
	rolesToDisplay: NurseFullRole[],
	cnaRatioTarget: number,
	nursesRatioTarget: number,
	timezone: string,
	isLowry: boolean,
	jobManagementEnabled: boolean,
) {
	for (let dayIndex = 0; dayIndex < context.currWeek.weeklyShifts[0].days.length; dayIndex++) {
		let census: number | undefined;
		let date: DateTime | undefined;
		// Get census and date from first shift since they're the same across shifts
		const firstDay = context.currWeek.weeklyShifts[0].days[dayIndex];
		census = firstDay.census_low;
		if (!census) continue;
		date = firstDay.date;

		// TODO: We count here the shift manager, not sure it's correct
		// Hours here feed the HPPD checks (regulation minimum and target), so they follow the department's switches.
		const nursesHoursData = getNursesHoursData(context.currWeek, dayIndex, rolesToDisplay, trainingPpdCountingOf(context.chosenDepartment));
		// The suggested role comes from patients-per-nurse headcounts, which never follow the HPPD/PPD switches.
		const headcountData = getNursesHoursData(context.currWeek, dayIndex, rolesToDisplay, COUNT_ALL_TRAINING);
		const totalTargetHours = hppdTargetForDate(context.chosenDepartment.hppd_target, date.weekday);

		const isNotNursesRegulationCompliant = !isNursesRegulationCompliant(nursesHoursData, census);
		const isNotCNARegulationCompliant = !isCNARegulationCompliant(nursesHoursData, census);
		if (isNotNursesRegulationCompliant || isNotCNARegulationCompliant) {
			const roleToAdd = isNotCNARegulationCompliant
				? [NurseRole.CNA]
				: [
						NurseRole.RN,
						...(departmentNurseTypes.includes(NurseFullRole.LPN) ? [NurseRole.LPN] : []),
						...(departmentNurseTypes.includes(NurseFullRole.LVN) ? [NurseRole.LVN] : []),
					];
			const issue = createRegulationComplaintIssue(context, dayIndex, date, roleToAdd, timezone);
			issues.push(issue);
		} else if (isUnderstaffedSnf(nursesHoursData, census, totalTargetHours)) {
			const issue = createSnfUnderstaffedIssue(
				context,
				census,
				dayIndex,
				date,
				cnaRatioTarget,
				nursesRatioTarget,
				headcountData,
				departmentNurseTypes,
				timezone,
				isLowry,
			);
			issues.push(issue);
		} else if (isOverstaffedSnf(nursesHoursData, census, totalTargetHours)) {
			const issue = createSnfOverstaffedIssue(
				context,
				census,
				dayIndex,
				date,
				cnaRatioTarget,
				nursesRatioTarget,
				headcountData,
				departmentNurseTypes,
				timezone,
				isLowry,
				jobManagementEnabled,
			);
			if (issue !== null) {
				issues.push(issue);
			}
		}
	}
}

function getAllActiveOpenShiftRequestsUntilEndOfWeek(requests: (OpenShiftRequest | ReplaceShiftRequest)[], lastDayOfWeek: DateTime) {
	const today = DateTime.now();
	return requests.filter(
		(request) =>
			request.status === RequestStatus.Active &&
			request.request_type === NotificationType.OpenShift &&
			request.message.date.startOf("day") >= today.startOf("day") &&
			request.message.date.startOf("day") <= lastDayOfWeek.startOf("day"),
	);
}

function setNurseShiftsAndAgencyNurses(context: StaffingContext) {
	const nurseCount: Map<number, Array<ShiftDetails>> = new Map();
	const agencyNurses: Map<number, Array<ShiftDetails>> = new Map();
	const currentDate = DateTime.now();
	for (let shiftNo = 0; shiftNo < context.currWeek.weeklyShifts.length; shiftNo++) {
		for (let dayNo = 0; dayNo < context.currWeek.weeklyShifts[shiftNo].days.length; dayNo++) {
			const day = context.currWeek.weeklyShifts[shiftNo].days[dayNo];
			const date = day.date;
			// Check each nurse in the shift
			for (const nurse of day.nurses) {
				const nurseData = context.nurseMap.get(nurse.nurseId);
				if (nurseData && !nurse.isPendingToDelete) {
					if (date >= currentDate && (nurseData.employmentType === EmploymentType.PerDiem || nurseData.employmentType === EmploymentType.Travel)) {
						const existingShifts = agencyNurses.get(nurse.nurseId) || [];
						agencyNurses.set(nurse.nurseId, [
							...existingShifts,
							{
								weekIndex: context.weekIndex,
								shiftNo: shiftNo,
								dayNo: dayNo,
								date: day.date,
								startTime: nurse.startTime,
								endTime: nurse.endTime,
							},
						]);
					} else {
						updateNurseCount(nurseCount, nurse.nurseId, context.weekIndex, shiftNo, dayNo, day.date, nurse.startTime, nurse.endTime);
					}
				}
			}
		}
	}
	return { nurseCount, agencyNurses };
}

function getRequestDatesAndNurses(allActiveRequests: (OpenShiftRequest | ReplaceShiftRequest)[]) {
	// Extract all request dates and nurses that picked them
	const requestDatesAndNurses = {
		NoResponse: allActiveRequests
			.filter((request) => !request.message.request_data.some((data) => data.status === RequestResponsesStatusOptions.PENDING))
			.map((request) => request),
		Response: allActiveRequests
			.filter((request) => request.message.request_data.some((data) => data.status === RequestResponsesStatusOptions.PENDING))
			.map((request) => request),
	};
	return requestDatesAndNurses;
}

function handleRequestWithResponseRecommendation(
	context: StaffingContext,
	issues: StaffingIssue[],
	shiftDate: DateTime,
	shiftDetails: ShiftDetails,
	nurseId: number,
	pickedShiftNurses: number[],
	requestId: string,
	shiftType: ShiftType,
	timezone: string,
) {
	// Find the nurse in the context
	const nurse = context.nurseMap.get(nurseId);
	if (!nurse) return;

	// Get the shift details
	const { weekIndex, dayNo, shiftNo } = shiftDetails;
	const shiftTimes = context.calculateShiftTimes(context.currWeek.weeklyShifts[shiftNo].days[dayNo], shiftNo, nurse.role);
	// Create a staffing issue for a request with response
	const issue: StaffingIssue = {
		suggestions: [],
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		shiftTimes: {
			date: shiftDate,
			shiftType: shiftType,
			start: shiftTimes.startTime,
			end: shiftTimes.endTime,
		},
		title: [
			{ type: "bold", text: `${shiftDate.toFormat("cccc")}` },
			{ type: "bold", text: `${NurseFullRoleShort[nurse.role]}` },
			{ type: "bold", text: formatNurseName(nurse, context.lastNameFirst) },
			{ type: "normal", text: "that can be" },
			{ type: "bold", text: "replaced" },
			{
				type: "normal",
				text: "there is an open shift request for this shift with",
			},
			{ type: "bold", text: `${pickedShiftNurses.length} responses` },
		],
	};

	// Add suggestions for each available nurse
	pickedShiftNurses.forEach((pickedShiftNurseId) => {
		const pickedShiftNurse = context.nurseMap.get(pickedShiftNurseId);
		if (!pickedShiftNurse) return;

		issue.suggestions.push({
			action: SuggestionAction.Replace,
			nurseId: nurseId,
			otherNurseId: pickedShiftNurseId,
			weekIndex: weekIndex,
			dayNo: dayNo,
			shiftNo: shiftNo,
			otherDayNo: dayNo,
			otherShiftNo: shiftNo,
			requestId: requestId,
		});
	});

	issues.push(issue);
}

function handleRequestWithoutResponseRecommendation(
	context: StaffingContext,
	issues: StaffingIssue[],
	shiftDate: DateTime,
	nurseId: number,
	shiftNo: number,
	dayNo: number,
	shiftType: ShiftType,
) {
	const nurse = context.nurseMap.get(nurseId);
	if (!nurse) return;
	const shiftTimes = context.calculateShiftTimes(context.currWeek.weeklyShifts[shiftNo].days[dayNo], shiftNo, nurse.role);
	const issue: StaffingIssue = {
		suggestions: [],
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		shiftTimes: {
			shiftType: shiftType,
			date: shiftDate,
			start: shiftTimes.startTime,
			end: shiftTimes.endTime,
		},
		title: [
			{ type: "bold", text: `${shiftDate.toFormat("cccc")}` },
			{ type: "bold", text: `${NurseFullRoleShort[nurse.role]}` },
			{ type: "bold", text: formatNurseName(nurse, context.lastNameFirst) },
			{ type: "normal", text: "that can be" },
			{ type: "bold", text: "replaced" },
			{ type: "normal", text: "there is an open shift request for this shift" },
			{ type: "bold", text: "without response yet" },
		],
	};
	issues.push(issue);
}

function handleUpdateNeededNursesRecommendation(
	issues: StaffingIssue[],
	date: DateTime,
	role: NurseFullRole,
	count: { actualNeeded: number; actualRequested: number },
	shiftType: ShiftType,
	startTime: DateTime,
	endTime: DateTime,
) {
	const issue: StaffingIssue = {
		suggestions: [],
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		shiftTimes: {
			shiftType: shiftType,
			date: date,
			start: startTime,
			end: endTime,
		},
		title: [
			{ type: "bold", text: `${date} ` },
			{
				type: "normal",
				text: "needed to update the number of staff needed for",
			},
			{ type: "bold", text: `post new open shift ${role} - ` },
			{ type: "bold", text: `${count.actualNeeded}` },
			{ type: "normal", text: "staff needed" },
			{
				type: "bold",
				text: `${startTime.toFormat("HH:mm")} - ${endTime.toFormat("HH:mm")}`,
			},
		],
	};
	issues.push(issue);
}

function handleOpenShiftRequestRecommendation(
	issues: StaffingIssue[],
	date: DateTime,
	role: string,
	count: number,
	shiftType: ShiftType,
	startTime: DateTime,
	endTime: DateTime,
) {
	const issue: StaffingIssue = {
		suggestions: [],
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		shiftTimes: {
			shiftType: shiftType,
			date: date,
			start: startTime,
			end: endTime,
		},
		title: [
			{ type: "bold", text: `${date} ` },
			{ type: "normal", text: "contains staff that can be" },
			{ type: "bold", text: "replaced" },
			{
				type: "bold",
				text: `post new open shift ${role in NurseFullRoleShort ? NurseFullRoleShort[role as keyof typeof NurseFullRoleShort] : role} - ${count} needed, `,
			},
			{
				type: "bold",
				text: `${startTime.toFormat("HH:mm")} - ${endTime.toFormat("HH:mm")}`,
			},
		],
	};
	issues.push(issue);
}

function getOverstaffedNurses(context: StaffingContext, nurseCount: Map<number, Array<ShiftDetails>>) {
	return (
		Array.from(nurseCount.entries())
			.filter(([nurseID, shifts]) => {
				const nurse = context.nurseMap.get(nurseID);
				const allowedShifts = nurse?.allowedShifts ?? 0;
				return shifts.length > allowedShifts;
			})
			.map(([nurseID, shifts]) => {
				// Then filter to only future shifts for each overstaffed nurse
				const today = DateTime.now();
				const futureShifts = shifts.filter((shift) => shift.date && (shift.date > today.endOf("day") || shift.date.startOf("day") === today.startOf("day")));
				return [nurseID, futureShifts] as [number, Array<ShiftDetails>];
			})
			// Finally filter out nurses with no future shifts
			.filter(([_, futureShifts]) => futureShifts.length > 0)
	);
}

function findMatchForReplaceRecommendationAndRequest(
	context: StaffingContext,
	issues: StaffingIssue[],
	replacementRecommendationsNurses: Array<[number, Array<ShiftDetails>]>,
	ExistsReuqestsShiftsRoleNeedsCountMap: Map<string, { actualNeeded: number; actualRequested: number }>,
	newPostOpenShiftRoleNeedsMap: Map<string, number>,
	requestWithResponse: any,
	requestWithoutResponse: any,
	timezone: string,
	isLowry: boolean,
) {
	const updateRoleNeedsMap = (
		ExistsReuqestsShiftsRoleNeedsCountMap: Map<string, { actualNeeded: number; actualRequested: number }>,
		key: string,
		requestedNurses: number,
	) => {
		const currentCount = ExistsReuqestsShiftsRoleNeedsCountMap.get(key) || {
			actualNeeded: 0,
			actualRequested: requestedNurses,
		};
		ExistsReuqestsShiftsRoleNeedsCountMap.set(key, {
			actualNeeded: currentCount.actualNeeded + 1,
			actualRequested: currentCount.actualRequested,
		});
	};
	const isMatchingShift = (req: any, shiftDate: DateTime, shiftType: ShiftType, role: string, startTime: DateTime, endTime: DateTime) =>
		req?.date?.isSame(shiftDate, "day") &&
		req.shiftType === shiftType &&
		req.role.includes(role) &&
		req.startTime &&
		req.endTime &&
		req.startTime.isSame(startTime, "minute") &&
		req.endTime.isSame(endTime, "minute");

	for (const [nurseId, shiftsDetails] of replacementRecommendationsNurses) {
		for (const shiftDetails of shiftsDetails) {
			const { shiftNo, dayNo, date, startTime, endTime } = shiftDetails;
			const shiftType: ShiftType = shiftNo === 0 ? ShiftType.Day : ShiftType.Night;
			const nurseData: Nurse | undefined = context.nurseMap.get(nurseId);

			if (!nurseData) {
				continue;
			}

			// Determine role for the key
			const roleShort: string = NurseFullRoleShort[nurseData.role];
			const role = roleShort === NurseRole.RN || roleShort === NurseRole.LPN || roleShort === NurseRole.LVN ? IssueType.Nurse : roleShort;

			// Create a unique key for this shift
			// Define interface for shift key components

			// Create a string
			const keyString: string = JSON.stringify({
				role,
				date,
				shiftType,
				startTime,
				endTime,
			});

			// Check if there's a request with response for this shift
			const matchingResponseRequest = requestWithResponse?.find((req: any) => isMatchingShift(req, date, shiftType, role, startTime, endTime));

			if (matchingResponseRequest) {
				// Handle request with nurses that picked it
				handleRequestWithResponseRecommendation(
					context,
					issues,
					date,
					shiftDetails,
					nurseId,
					matchingResponseRequest.pickedShiftNurses,
					matchingResponseRequest.requestId,
					shiftType,
					timezone,
				);
				updateRoleNeedsMap(ExistsReuqestsShiftsRoleNeedsCountMap, keyString, matchingResponseRequest.neededNurses);
				continue;
			}

			// Check if there's a request without response for this shift
			const matchingNoResponseRequest = requestWithoutResponse?.find((req: any) => isMatchingShift(req, date, shiftType, role, startTime, endTime));

			if (matchingNoResponseRequest) {
				// Handle request with no nurses that picked it
				handleRequestWithoutResponseRecommendation(context, issues, date, nurseId, shiftNo, dayNo, shiftType);
				updateRoleNeedsMap(ExistsReuqestsShiftsRoleNeedsCountMap, keyString, matchingNoResponseRequest.neededNurses);
				continue;
			}

			// No existing request for this shift - need to create a new one
			const currentCount = newPostOpenShiftRoleNeedsMap.get(keyString) || 0;
			newPostOpenShiftRoleNeedsMap.set(keyString, currentCount + 1);
		}
	}
}

function handleReplacementRecommendations(
	context: StaffingContext,
	issues: StaffingIssue[],
	replacementRecommendationsNurses: Array<[number, Array<ShiftDetails>]>,
	requestDatesAndNurses: any,
	timezone: string,
	isLowry: boolean,
) {
	const setRole = (roles: string[]) => {
		return [
			...new Set(
				roles.map((role: string) => {
					return role === NurseFullRole.RN || role === NurseFullRole.LPN || role === NurseFullRole.LVN
						? IssueType.Nurse
						: NurseFullRoleShort[role as NurseFullRole];
				}),
			),
		];
	};

	const requestWithResponse = requestDatesAndNurses.Response.map((r: OpenShiftRequest) => ({
		date: r.message.date,
		shiftType: r.message.shift_type,
		neededNurses: r.needed_nurses,
		role: setRole(r.message.roles),
		pickedShiftNurses: r.message.request_data.filter((data) => data.status === RequestResponsesStatusOptions.PENDING).map((data) => data.nurse_id),
		requestId: r.id,
		startTime: r.message.shift_start,
		endTime: r.message.shift_end,
	}));

	const requestWithoutResponse = requestDatesAndNurses.NoResponse.map((r: OpenShiftRequest) => ({
		date: r.message.date,
		shiftType: r.message.shift_type,
		neededNurses: r.needed_nurses,
		role: setRole(r.message.roles),
		requestId: r.id,
		startTime: r.message.shift_start,
		endTime: r.message.shift_end,
	}));

	const newPostOpenShiftRoleNeedsMap = new Map<string, number>();

	const ExistsReuqestsShiftsRoleNeedsCountMap = new Map<string, { actualNeeded: number; actualRequested: number }>();

	findMatchForReplaceRecommendationAndRequest(
		context,
		issues,
		replacementRecommendationsNurses,
		ExistsReuqestsShiftsRoleNeedsCountMap,
		newPostOpenShiftRoleNeedsMap,
		requestWithResponse,
		requestWithoutResponse,
		timezone,
		isLowry,
	);

	// Process existing requests that need more nurses than currently requested
	for (const [key, count] of ExistsReuqestsShiftsRoleNeedsCountMap.entries()) {
		if (count.actualNeeded > count.actualRequested) {
			const parsedData = JSON.parse(key);
			const { role, shiftType } = parsedData;
			const date = parsedData.date.setZone(timezone);
			const startTime = parsedData.startTime.setZone(timezone);
			const endTime = parsedData.endTime.setZone(timezone);

			handleUpdateNeededNursesRecommendation(issues, date, NurseFullRole[role as keyof typeof NurseRole], count, shiftType as ShiftType, startTime, endTime);
		}
	}

	// Create recommendations for new open shift requests
	for (const [key, count] of newPostOpenShiftRoleNeedsMap.entries()) {
		const parsedData = JSON.parse(key);
		const { role, shiftType } = parsedData;
		const date = parsedData.date.setZone(timezone);
		const startTime = parsedData.startTime.setZone(timezone);
		const endTime = parsedData.endTime.setZone(timezone);
		handleOpenShiftRequestRecommendation(issues, date, role, count, shiftType as ShiftType, startTime, endTime);
	}
}

function getSnfShiftStaffingIssues(
	context: StaffingContext,
	departmentNurseTypes: NurseFullRole[],
	rolesToDisplay: NurseFullRole[],
	timezone: string,
	isLowry: boolean,
	jobManagementEnabled = false,
) {
	const issues: StaffingIssue[] = [];

	const { cnaRatioTarget, nursesRatioTarget } = getSnfShiftRatioTarget(context.chosenDepartment);

	getOverstaffedUnderstaffedIssues(
		context,
		issues,
		departmentNurseTypes,
		rolesToDisplay,
		cnaRatioTarget,
		nursesRatioTarget,
		timezone,
		isLowry,
		jobManagementEnabled,
	);

	return issues;
}

function getShiftStaffingIssues(
	context: StaffingContext,
	{
		isSnf,
		isAutoScheduler,
		timezone,
		isLowry,
	}: {
		isSnf: boolean;
		isAutoScheduler: boolean;
		timezone: string;
		isLowry: boolean;
	},
) {
	const issues: StaffingIssue[] = [];

	for (let shiftNo = 0; shiftNo < context.currWeek.weeklyShifts.length; shiftNo++) {
		const weeklyShifts = context.currWeek.weeklyShifts[shiftNo];
		const understaffedIssues: StaffingIssue[] = [];
		const overstaffedIssues: StaffingIssue[] = [];
		for (let dayIndex = 0; dayIndex < weeklyShifts.days.length; dayIndex++) {
			const day = weeklyShifts.days[dayIndex];
			const understaffed = isUnderstaffed(day);
			const overstaffed = isOverstaffed(day);

			if (understaffed || overstaffed) {
				let priority: IssuePriority | undefined;
				const currentNurses = day.nurses.filter((nurse: NurseInShift) => !nurse.isPendingToDelete).length;
				const suggestions: Suggestion[] = [];
				let nursesToModify: Nurse[] = [];
				const action: SuggestionAction = understaffed ? SuggestionAction.Add : SuggestionAction.Remove;

				if (Math.abs(day.staffNo - currentNurses) >= 2) {
					priority = IssuePriority.TopPriority;
				}
				const isSortForRemoval = !understaffed;
				nursesToModify = sortNurses(
					context.hospitalData,
					shiftNo,
					context.weekIndex,
					dayIndex,
					context.nurseOcuurences,
					context.calculateShiftTimes,
					isSortForRemoval,
					NurseRole.RN,
					isAutoScheduler,
				);
				if (isAutoScheduler) {
					// remove nurses that are shift managers or per diem (PRN)
					nursesToModify = nursesToModify.filter((nurse) => !nurse.isShiftManager && nurse.employmentType !== EmploymentType.PerDiem);
				}

				for (let nurseIndex = 0; nurseIndex < nursesToModify.length; nurseIndex++) {
					suggestions.push({
						action,
						nurseId: nursesToModify[nurseIndex].id,
						shiftNo: shiftNo,
						dayNo: dayIndex,
						weekIndex: context.weekIndex,
					});
					if (priority === undefined) {
						const nurseGroup = determineGroupIndex(
							nursesToModify[nurseIndex],
							context.hospitalData,
							shiftNo,
							context.weekIndex,
							dayIndex,
							context.nurseOcuurences,
							context.calculateShiftTimes,
						);
						if (
							(understaffed && nurseGroup < NurseGroup.FullTimeOrPartTimeFullyBooked) ||
							(overstaffed && nurseGroup > NurseGroup.FullTimeOrPartTimeFullyBooked)
						) {
							priority = IssuePriority.TopPriority;
						}
					}
				}
				const shiftTimes = context.calculateShiftTimes(day, shiftNo, nursesToModify[0]?.role);
				const issue: StaffingIssue = {
					issueType: IssueType.Shift,
					priority: IssuePriority.TopPriority,
					suggestions: suggestions,
					shiftTimes: {
						start: shiftTimes.startTime,
						end: shiftTimes.endTime,
						date: day.date,
						shiftType: weeklyShifts.type as ShiftType,
					},
					title: [
						{
							type: "bold",
							text: `${day.date.toFormat("cccc")} ${weeklyShifts.type} `,
						},
						{ type: "normal", text: "is " },
						{
							type: "bold",
							text: `${understaffed ? "understaffed " : "overstaffed "}, `,
						},
						{ type: "normal", text: "consider " },
						{
							type: "normal",
							text: understaffed ? "assigning additional " : "removing one ",
						},
						{ type: "normal", text: "employee" },
					],
				};
				if (understaffed) {
					understaffedIssues.push(issue);
				} else {
					overstaffedIssues.push(issue);
				}
			}
		}
		issues.push(...(overstaffedIssues || []), ...(understaffedIssues || []));
	}
	return issues;
}

export const getNurseIdsForShift = (hospitalData: ScheduleData, day: number, shift: number, weekIndex: number) => {
	if (day < 0 || day >= hospitalData.weeks[weekIndex].weeklyShifts[shift].days.length) {
		return [];
	}
	return hospitalData.weeks[weekIndex].weeklyShifts[shift].days[day].nurses.map((nurse: NurseInShift) => nurse.nurseId);
};

export const isWeekend = (date: DateTime, shiftType: ShiftType) => {
	const dayOfWeek: string = date.toFormat("cccc");
	if (shiftType === ShiftType.Day) {
		return ["Saturday", "Sunday"].includes(dayOfWeek);
	}
	return ["Friday", "Saturday", "Sunday"].includes(dayOfWeek);
};

export const getNurseShiftOccurrencesByWeekend = (hospitalData: ScheduleData): Record<number, number> | undefined => {
	const nurseWeekendOccurrences: Record<number, number> = {};

	// Ensure there are valid weeks data
	if (!hospitalData?.weeks || hospitalData.weeks.length === 0) {
		return nurseWeekendOccurrences;
	}

	// Loop through all weeks
	for (const week of hospitalData.weeks) {
		const weekShifts = week.weeklyShifts;

		if (!weekShifts) {
			continue;
		}

		for (const shift of weekShifts) {
			for (let dayIndex = 0; dayIndex < shift.days.length; dayIndex++) {
				const day = shift.days[dayIndex];
				if (isWeekend(day.date, shift.type as ShiftType)) {
					for (const nurse of day.nurses) {
						if (nurse.isPendingToDelete) continue;

						const nurseId = nurse.nurseId;
						if (!(nurseId in nurseWeekendOccurrences)) {
							nurseWeekendOccurrences[nurseId] = 0;
						}
						nurseWeekendOccurrences[nurseId]++;
					}
				}
			}
		}
	}
	return nurseWeekendOccurrences;
};

export const getNurseShiftOccurrencesByWeek = (hospitalData: ScheduleData, weekIndex: number): Record<number, number> => {
	const nurseOccurrences: Record<number, number> = {};

	if (!hospitalData?.weeks || weekIndex < 0 || weekIndex >= hospitalData.weeks.length) {
		return nurseOccurrences;
	}

	const weekShifts = hospitalData.weeks[weekIndex]?.weeklyShifts;
	if (!weekShifts) {
		return nurseOccurrences;
	}

	for (const shift of weekShifts) {
		for (const day of shift.days) {
			for (const nurse of day.nurses.filter((nurse) => !nurse.isPendingToDelete)) {
				const nurseId = nurse.nurseId;
				if (!(nurseId in nurseOccurrences)) {
					nurseOccurrences[nurseId] = 0;
				}
				nurseOccurrences[nurseId]++;
			}
		}
	}

	return nurseOccurrences;
};

export const isNurseScheduledAdjacent = (hospitalData: any, nurseId: number, dayNo: number, shiftTime: number, weekIndex: number) => {
	const weeks = hospitalData.weeks;
	const currentWeek = weeks[weekIndex];
	const numDays = currentWeek.weeklyShifts[0].days.length; // assuming each week has the same number of days
	const numShifts = currentWeek.weeklyShifts.length; // number of shifts per day

	const previousShift = shiftTime === 0 ? numShifts - 1 : shiftTime - 1;
	const nextShift = shiftTime === numShifts - 1 ? 0 : shiftTime + 1;

	let previousDay = shiftTime === 0 ? dayNo - 1 : dayNo;
	let nextDay = shiftTime === numShifts - 1 ? dayNo + 1 : dayNo;

	let previousWeekIndex = weekIndex;
	let nextWeekIndex = weekIndex;

	// Adjust for week boundaries
	if (previousDay < 0) {
		previousWeekIndex = weekIndex - 1;
		if (previousWeekIndex >= 0) {
			previousDay = weeks[previousWeekIndex].weeklyShifts[0].days.length - 1;
		}
	}
	if (nextDay >= numDays) {
		nextWeekIndex = weekIndex + 1;
		if (nextWeekIndex < weeks.length) {
			nextDay = 0;
		}
	}
	const previousShiftNurseIds =
		previousWeekIndex >= 0 && previousWeekIndex < weeks.length && previousDay >= 0
			? getNurseIdsForShiftNotPendingDeletion(hospitalData, previousDay, previousShift, previousWeekIndex)
			: [];
	const nextShiftNurseIds =
		nextWeekIndex >= 0 && nextWeekIndex < weeks.length && nextDay < numDays
			? getNurseIdsForShiftNotPendingDeletion(hospitalData, nextDay, nextShift, nextWeekIndex)
			: [];

	return previousShiftNurseIds.includes(nurseId) || nextShiftNurseIds.includes(nurseId);
};

const getNurseIdsForShiftNotPendingDeletion = (hospitalData: any, day: number, shift: number, weekIdx: number) => {
	// Retrieve the array of nurses for a given day and shift in a specific week
	const nurses = hospitalData.weeks[weekIdx].weeklyShifts[shift].days[day]?.nurses;

	// Filter out nurses who are marked as pending deletion and match a specific nurse ID
	return nurses ? nurses.filter((nurse: any) => !nurse.isPendingToDelete).map((nurse: any) => nurse.nurseId) : [];
};
